import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ArrowRight, CheckCircle2, Radar, Sparkles, Dna, Library, BarChart3, Zap, BrainCircuit } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);

  const designImages = [
    {
      url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80',
      label: 'Creative Performance Intelligence',
      sub: 'GPT-4o decodes every creative — themes, hooks, colors, CTAs',
    },
    {
      url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=80',
      label: 'Competitor Radar',
      sub: 'Live Meta Ads scrape · learn what your rivals are running today',
    },
    {
      url: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&q=80',
      label: 'SKU DNA · Brand Repository',
      sub: 'Single source of truth for prices, dimensions, EAN, brand assets',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % designImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleGoogleLogin = () => {
    login();
  };

  return (
    <div className="min-h-screen flex overflow-hidden" data-testid="login-page">
      {/* Left side - Brand & Visual */}
      <div className="hidden lg:flex lg:w-[60%] relative overflow-hidden bg-[#0A0F1A]">
        {/* Background Gradient */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#2EB4AC]/20 via-transparent to-blue-600/10" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#2EB4AC]/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[120px]" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full w-full p-10 xl:p-14">
          {/* Logo - Large & Prominent */}
          <div className="mb-8">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2EB4AC] to-[#20A39E] flex items-center justify-center shadow-2xl shadow-[#2EB4AC]/30 flex-shrink-0">
                <span className="font-black text-3xl text-white">P</span>
              </div>
              <div className="pt-1">
                <h1 className="font-black text-5xl text-[#2EB4AC] tracking-tight leading-none">
                  PRISM
                </h1>
                {/* Wellversed Parent Brand */}
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="text-slate-500 text-sm">by</span>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dafbd9f2-4a1c-4bf6-a0fc-53687b91e9c2/artifacts/xpqc0ett_Wellversed%20Complete%20Logo%20Blue%20no-tagline%20%282%29.png" 
                    alt="Wellversed" 
                    className="h-6 object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Eyebrow tag */}
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full border border-[#2EB4AC]/30 bg-[#2EB4AC]/10 self-start">
            <BrainCircuit className="h-3.5 w-3.5 text-[#2EB4AC]" />
            <span className="text-[11px] font-semibold tracking-[0.18em] text-[#2EB4AC] uppercase">
              Catalog · Creative Intelligence
            </span>
          </div>

          {/* Main Headline */}
          <div className="mb-10">
            <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
              From every SKU you list
              <br />
              <span className="bg-gradient-to-r from-[#2EB4AC] via-emerald-400 to-[#2EB4AC] bg-clip-text text-transparent">
                to every ad you launch.
              </span>
            </h2>
            <p className="text-slate-400 text-lg max-w-lg leading-relaxed">
              The AI-powered command center for your catalog &amp; creative ops — analyze, compare, and decide faster.
            </p>
          </div>

          {/* Image Gallery Section */}
          <div className="flex-1 flex items-center">
            <div className="w-full grid grid-cols-12 gap-4">
              {/* Main Image - Large */}
              <div className="col-span-7 relative">
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                  {designImages.map((img, idx) => (
                    <div
                      key={idx}
                      className={`absolute inset-0 transition-opacity duration-1000 ${
                        idx === currentSlide ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={img.label}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    </div>
                  ))}
                  {/* Label */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-semibold">{designImages[currentSlide].label}</p>
                    <p className="text-white/70 text-xs mt-0.5">{designImages[currentSlide].sub}</p>
                  </div>
                  {/* Dots Indicator */}
                  <div className="absolute bottom-4 right-4 flex gap-1.5">
                    {designImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentSlide ? 'bg-[#2EB4AC] w-6' : 'bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Side Cards */}
              <div className="col-span-5 flex flex-col gap-4">
                {/* Intelligence Modules Card */}
                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold">Intelligence Modules</h3>
                    <span className="text-[9px] font-bold tracking-[0.15em] text-[#2EB4AC] uppercase px-1.5 py-0.5 rounded bg-[#2EB4AC]/10 border border-[#2EB4AC]/20">Live</span>
                  </div>
                  <div className="space-y-3">
                    {[
                      { icon: Sparkles, label: 'Creative Analyzer', count: 'GPT-4o' },
                      { icon: Radar, label: 'Competitor Radar', count: 'Meta' },
                      { icon: Library, label: 'Brand Repository', count: 'Tagged' },
                      { icon: Dna, label: 'SKU DNA · Catalog', count: 'Live' },
                      { icon: BarChart3, label: 'Performance Intel', count: 'Auto' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#2EB4AC]/10 flex items-center justify-center">
                            <item.icon className="h-4 w-4 text-[#2EB4AC]" />
                          </div>
                          <span className="text-slate-300 text-sm">{item.label}</span>
                        </div>
                        <span className="text-white/70 font-medium text-[10px] tracking-wider uppercase">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Powered-by AI Card */}
                <div className="bg-gradient-to-br from-[#2EB4AC] to-[#20A39E] rounded-2xl p-5 relative overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="h-4 w-4 text-white" />
                      <h3 className="text-white font-semibold text-sm tracking-wide">Powered by</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['GPT-4o', 'Apify', 'Gemini', 'Claude'].map((tech) => (
                        <span key={tech} className="px-2.5 py-1 bg-white/20 backdrop-blur rounded-full text-white text-[11px] font-semibold">
                          {tech}
                        </span>
                      ))}
                    </div>
                    <p className="text-white/80 text-[11px] mt-3 leading-relaxed">
                      AI vision, scraping &amp; reasoning — stitched into one workspace.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom - Feature Pills */}
          <div className="pt-8 mt-auto border-t border-white/5">
            <div className="flex items-center gap-3 flex-wrap">
              {[
                'AI Creative Analysis',
                'Meta Ads Scraping',
                'Actionable Summaries',
                'Brand Repository',
                'SKU DNA · Pricing',
                'Cost Tracker',
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#2EB4AC]" />
                  <span className="text-slate-300 text-xs">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Sign In Form */}
      <div className="w-full lg:w-[40%] flex flex-col justify-center items-center p-8 lg:p-12 bg-gradient-to-b from-white to-slate-50 relative">
        {/* Subtle Background */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(#2EB4AC 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }} />

        <div className="w-full max-w-sm relative z-10">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2EB4AC] to-[#20A39E] flex items-center justify-center">
                <span className="font-black text-xl text-white">P</span>
              </div>
              <div className="text-left">
                <span className="font-black text-2xl block">
                  PRISM
                </span>
                <span className="text-slate-400 text-[10px] tracking-widest uppercase">Catalog · Creative Intelligence</span>
              </div>
            </div>
          </div>

          {/* Welcome Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-[#2EB4AC]/10 to-[#2EB4AC]/5 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2EB4AC] to-[#20A39E] flex items-center justify-center shadow-lg shadow-[#2EB4AC]/25">
                <span className="font-black text-white text-xl">P</span>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              Welcome back
            </h2>
            <p className="text-slate-500">
              Sign in to manage your design tasks
            </p>
          </div>

          {/* Access Notice */}
          <div className="mb-6 p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#2EB4AC] to-[#20A39E] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#2EB4AC]/20">
                <ShieldCheck className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Restricted Access</p>
                <p className="text-sm text-slate-500">
                  Only <span className="font-bold text-[#2EB4AC]">@wellversed.in</span> emails
                </p>
              </div>
            </div>
          </div>

          {/* Google Button */}
          <Button
            onClick={handleGoogleLogin}
            className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-base rounded-2xl shadow-xl shadow-slate-900/10 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-900/20 hover:-translate-y-0.5 group"
            data-testid="google-login-btn"
          >
            <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
            <ArrowRight className="ml-2 h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
          </Button>

          {/* Divider */}
          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] text-slate-400 font-medium tracking-wider">SECURE SSO</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Trust */}
          <div className="flex items-center justify-center gap-6 text-slate-400">
            <div className="flex items-center gap-1.5 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 text-[#2EB4AC]" />
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 text-[#2EB4AC]" />
              <span>Enterprise SSO</span>
            </div>
          </div>

          <p className="text-center text-[11px] text-slate-400 mt-6">
            By signing in, you agree to use PRISM with your Wellversed credentials
          </p>

          {/* Mobile Features */}
          <div className="lg:hidden mt-8 pt-6 border-t border-slate-200">
            <div className="flex flex-wrap justify-center gap-2">
              {['Auto IDs', 'Notifications', 'Tracking'].map((f, i) => (
                <span key={i} className="px-3 py-1.5 bg-slate-100 rounded-full text-xs text-slate-600 font-medium">
                  {f}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
